<template>
  <div>
    <div v-if="loading">
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem"
        label="Large Spinner"
      ></b-spinner>
    </div>

    <b-row v-if="!loading">
      <b-col>
        <div class="row">
          <div class="col-md-8">
            <h3 class="mt-1 mb-0">{{ stakeholder.name }}</h3>

            <h5>{{ stakeholder.category }}</h5>

            <h5>
              <span
                class="badge"
                :class="{
                  'badge-success': stakeholder.status == 'Active',
                  'badge-danger': stakeholder.status == 'Obsolete',
                  'badge-warning': stakeholder.status == 'Inactive',
                }"
                >{{ stakeholder.status }}</span
              >
            </h5>

            <div class="my-3" v-if="userHasPerms(['Manage stakeholders'])">
              <router-link
                v-b-tooltip.hover
                title="Edit"
                :to="{
                  name: 'stakeholders-edit',
                  params: { id: stakeholder.id },
                }"
                class="text-primary table-action-icon ml-0"
              >
                <i class="lni lni-pencil"></i>
              </router-link>

              <span
                v-b-tooltip.hover
                style="cursor: pointer"
                title="Delete"
                @click="confirmDelete(stakeholder.id)"
                class="text-danger table-action-icon"
              >
                <i class="lni lni-trash"></i>
              </span>
            </div>
          </div>
          <div class="col-md-4 text-center text-md-right">
            <v-lazy-image
              v-if="stakeholder.image"
              :src="stakeholder.image"
              class="img-fluid maxHeight200 mb-2"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <b-tabs class="mt-3">
              <b-tab title="Details">
                <table class="table table-sm mt-2">
                  <table-row label="Current Tier" :noBorder="true"
                    >{{ stakeholder.current_tier }}
                  </table-row>

                  <table-row label="Desired Tier"
                    >{{ stakeholder.desired_tier }}
                  </table-row>

                  <table-row label="Note"
                    ><p class="nl2br">{{ stakeholder.note }}</p></table-row
                  >

                  <table-row label="Contact Person">
                    {{ stakeholder.contact_person }}
                  </table-row>

                  <table-row v-if="stakeholder.phone_1 != null" label="Phone"
                    ><a :href="'tel:' + stakeholder.phone_1">{{
                      stakeholder.phone_1
                    }}</a></table-row
                  >

                  <table-row v-if="stakeholder.phone_2 != null" label="Phone 2"
                    ><a :href="'tel:' + stakeholder.phone_2">{{
                      stakeholder.phone_2
                    }}</a></table-row
                  >

                  <table-row
                    v-if="stakeholder.email_address != null"
                    label="Email"
                    ><a :href="'mailto:' + stakeholder.email_address">{{
                      stakeholder.email_address
                    }}</a></table-row
                  >

                  <table-row label="Address">
                    <a
                      :href="
                        'https://www.google.com/maps/search/?api=1&query=' +
                        stakeholder.physical_address
                      "
                      target="new"
                      >{{ stakeholder.physical_address }}
                    </a>
                  </table-row>

                  <table-row icon="lni-database" :noBorder="true" />

                  <table-row label="Id">{{ stakeholder.id }} </table-row>

                  <table-row label="Created"
                    >{{
                      moment(new Date(stakeholder.created_at)).format(
                        "DD MMM YYYY, HH:mm:ss"
                      )
                    }}
                  </table-row>
                  <table-row label="Updated"
                    >{{
                      moment(new Date(stakeholder.updated_at)).format(
                        "DD MMM YYYY, HH:mm:ss"
                      )
                    }}
                  </table-row>
                </table>
              </b-tab>
              <b-tab title="Engagements">
                <div
                  class="row no-gutters border my-1"
                  v-for="engagement in stakeholder.engagements"
                  :key="engagement.id"
                >
                  <router-link
                    :to="{
                      name: 'engagements-show',
                      params: { id: engagement.id },
                    }"
                  >
                    <div class="col-12 p-3 text-decoration-none">
                      <div
                        class="
                          mt-1
                          py-1
                          font-weight-bold
                          text-center
                          font-size-11
                          text-white
                          d-inline
                          px-3
                          pointer
                        "
                        :class="{
                          'bg-success': engagement.status == 'Engaged',
                          'bg-danger': engagement.status == 'Cancelled',
                          'bg-warning': engagement.status == 'Created',
                        }"
                      >
                        {{ engagement.status }}
                      </div>

                      <br /><br />
                      <table class="table table-sm">
                        <table-row
                          label="Date"
                          :noBorder="true"
                          class="text-body"
                        >
                          {{
                            moment(new Date(engagement.engagement_date)).format(
                              "DD MMM YY, HH:mm"
                            )
                          }}
                        </table-row>
                        <table-row label="Mode"     :noBorder="true" class="text-body">
                          {{ engagement.mode }}
                        </table-row>
                        <table-row label="Outcome"     :noBorder="true" class="text-body">
                          {{ engagement.outcome }}
                        </table-row>
                      </table>
                    </div>
                  </router-link>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import stakeholderRepo from "@/api/repos/stakeholderRepo";
import tableRow from "@/components/common/TableRow";

export default {
  props: ["id"],

  components: {
    tableRow,
  },

  mounted() {
    this.getStakeholder();
  },

  data() {
    return {
      loading: false,
      stakeholder: {},
    };
  },

  methods: {
    async getStakeholder() {
      try {
        this.loading = true;
        let { data } = await stakeholderRepo.get(this.id);
        this.stakeholder = data.stakeholder;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async deleteStakeholder(id) {
      this.isBusy = true;
      try {
        await stakeholderRepo.delete(id);
        this.$emit("stakeholderUpdated");
      } catch (error) {
        console.log(error);
      } finally {
        this.isBusy = false;
      }
    },
    confirmDelete(id) {
      this.$bvModal
        .msgBoxConfirm(
          "Please confirm that you want to delete the stakeholder.",
          {
            title: "Please Confirm",
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((confirm) => (confirm ? this.deleteStakeholder(id) : false))
        .catch((error) => console.log(error));
    },
  },
};
</script>
